import moment from 'moment';

import { SESSION_RENEW_THRESHOLD } from 'Common/Constants';

/**
 * A class to help manage the user's token.
 */
export class TokenManager {
  _refreshError = '';
  /**
   * Create the token manager by passing it the token getter and setter functions.
   * @param {GetToken} getToken
   * @param {SetToken} setToken
   * @param {Function} refreshToken
   */
  constructor(getToken, setToken, postRefreshToken) {
    this._getToken = getToken;
    this._setToken = setToken;
    this._postRefreshToken = postRefreshToken;

    const token = getToken();
    if (!(token instanceof Object)) {
      throw new Error('getToken must return an Object.');
    }

    this.fill(token);
  }

  get accessToken() {
    return this._accessToken;
  }
  get refreshToken() {
    return this._refreshToken;
  }
  get tokenExpirationUTC() {
    return this._tokenExpirationUTC;
  }

  /**
   * return token values
   *
   * @return {Token}
   */
  get values() {
    return {
      tokenExpirationUTC: this._tokenExpirationUTC,
      accessToken: this._accessToken,
      refreshToken: this._refreshToken,
    };
  }

  /**
   * Checks that the token exists and isn't expired.
   * If the token is valid the user is considered logged in.
   *
   * @return  {boolean}
   */
  isValid() {
    return (
      this._accessToken &&
      this._tokenExpirationUTC &&
      this._accessToken.length > 0 &&
      moment.utc().isBefore(moment(this._tokenExpirationUTC))
    );
  }

  /**
   * @param {string|number} expiresInSeconds
   * @return {string}
   */
  static expiresInSecondsToUTC(expiresInSeconds) {
    return moment
      .utc()
      .add(expiresInSeconds, 'seconds')
      .format();
  }

  /**
   * Refresh the token.
   * @return {boolean} success
   */
  async refresh() {
    try {
      const tokenResponse = await this._postRefreshToken(this._refreshToken);
      const { expires_in, access_token, refresh_token } = tokenResponse;

      this.update({
        tokenExpirationUTC: TokenManager.expiresInSecondsToUTC(expires_in),
        accessToken: access_token,
        refreshToken: refresh_token,
      });
      return true;
    } catch (e) {
      this._refreshError = e;
      return false;
    }
  }

  /**
   * Update the token without saving the changes
   * @param {Token}
   */
  fill({ tokenExpirationUTC, accessToken, refreshToken }) {
    this._tokenExpirationUTC = tokenExpirationUTC;
    this._accessToken = accessToken;
    this._refreshToken = refreshToken;
  }

  /**
   * Update the token, save the changes.
   * @param {Token}
   */
  update(token) {
    this.fill(token);
    this.save();
  }

  /**
   * Save the changes, fire on change events.
   */
  save() {
    this._setToken(this.values);
    this._fireOnChangeCallbacks();
  }

  /**
   * Checks if the token expiration is within the SESSION_RENEW_THRESHOLD.
   * @returns {boolean}
   */
  isTimeToRenew() {
    return (
      !this._tokenExpirationUTC ||
      moment
        .utc()
        .isAfter(
          moment
            .utc(this._tokenExpirationUTC)
            .subtract(SESSION_RENEW_THRESHOLD, 'm')
        )
    );
  }

  /**
   * Returns a human readable version of when the token will expire.
   * If the token is invalid, such as when a token isn't set, it will return 'Invalid Token Expiration'
   * @returns {String}
   */
  expiresIn() {
    const expiresAt = moment(this._tokenExpirationUTC);

    return this._tokenExpirationUTC && expiresAt.isValid()
      ? expiresAt.fromNow()
      : 'Invalid Token Expiration';
  }

  onChangeListeners = [];

  /**
   * Subscirbe to token change events.
   * @param {onChange} onChangeCallback
   * @param {bool} callNow
   */
  addOnChangeListener(onChangeCallback, callNow = false) {
    this.onChangeListeners.push(onChangeCallback);

    if (callNow) {
      onChangeCallback(this.values);
    }
  }

  /**
   * Unsubscribe from token change events.
   * @param {onChange} onChangeCallback
   * @returns {bool} success
   */
  removeOnChangeListener(onChangeCallback) {
    const index = this.onChangeListeners.findIndex(c => c === onChangeCallback);
    return !!this.onChangeListeners.splice(index, 1);
  }

  /**
   * Publish the change event by calling all registered callbacks.
   */
  _fireOnChangeCallbacks() {
    this.onChangeListeners.forEach(callback => callback(this.values));
  }
}



// WEBPACK FOOTER //
// ./src/common/Security/TokenManager.js